import { IconChevronDown, IconClose, IconMail } from "@/assets/icons";
import { menus } from "@/constants/menu";
import { CONTACT_TYPE_DEFAULT } from "@/features/contact/constants/contact";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { AppRoutes } from "@/routes/config";
import { background, button, divider, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { hexToRgba } from "@/utils/pieces";
import { getDynamicRoute } from "@/utils/url";
import { Drawer, Stack, Tooltip, Typography, styled } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import zIndex from "@mui/material/styles/zIndex";
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";

type TAppDrawerProps = {
  isOpenDrawer: boolean;
  toggleDrawer: () => void;
};

// A leaf (navigable) menu item: has a path + relatedRoutes, no children.
type TLeafItem = {
  text: string;
  icon?: (props: React.SVGProps<SVGSVGElement>) => React.JSX.Element;
  path: string;
  relatedRoutes: readonly string[];
};

// Pure: no component-state closure, so callers don't risk a stale reference.
const isRouteActive = (routes: readonly string[], pathname: string) =>
  routes.some((route) => Boolean(matchRoutes([{ path: route }], pathname)));

export const AppDrawer: FC<TAppDrawerProps> = ({ isOpenDrawer, toggleDrawer }) => {
  const { isLaptop } = useMediaQuery();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Tracks which collapsible parent groups (keyed by parent text) are expanded.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Auto-expand a group when the current route belongs to one of its children.
  useEffect(() => {
    menus.forEach((item) => {
      if (typeof item === "string" || !("children" in item)) return;
      const hasActiveChild = item.children.some((child) => isRouteActive(child.relatedRoutes, pathname));
      if (hasActiveChild) setExpandedGroups((prev) => (prev[item.text] ? prev : { ...prev, [item.text]: true }));
    });
  }, [pathname]);

  const toggleGroup = (text: string) => setExpandedGroups((prev) => ({ ...prev, [text]: !prev[text] }));

  // Renders a navigable leaf item; reused for top-level items and group children.
  const renderLeafItem = useCallback(
    (item: TLeafItem, options?: { indent?: boolean }) => {
      const isActive = isRouteActive(item.relatedRoutes, pathname);
      if (isLaptop)
        return (
          <Tooltip key={item.text} title={item.text} placement="right">
            <ListItemWrapper isLaptop={isLaptop} className={isActive ? "active" : ""} onClick={() => navigate(item.path)}>
              <Stack flexDirection="row" justifyContent="center" alignItems="center">
                {item.icon && <item.icon width={24} height={24} />}
              </Stack>
            </ListItemWrapper>
          </Tooltip>
        );
      return (
        <ListItemWrapper
          isLaptop={isLaptop}
          key={item.text}
          className={isActive ? "active" : ""}
          onClick={() => navigate(item.path)}
          sx={options?.indent ? { ml: 2 } : undefined}
        >
          <Stack flexDirection="row" alignItems="center" gap={1.5}>
            {item.icon && <item.icon width={24} height={24} />}
            <Typography variant="body14Bold">{item.text}</Typography>
          </Stack>
        </ListItemWrapper>
      );
    },
    [pathname, isLaptop, navigate],
  );

  const DrawerContent = useMemo(
    () => (
      <ListWrapper isLaptop={isLaptop} gap={1}>
        {menus.map((item, index) => {
          if (typeof item === "string") {
            if (item === "|") return <Divider color={background.white} sx={{ opacity: 0.3 }} key={index} />;
            if (item === "booking") {
              return (
                <Stack key={index} flex={1} justifyContent="flex-end" marginTop={4} gap={1}>
                  {isLaptop ? (
                    <Tooltip title="お問い合わせ" placement="right">
                      <BookingButton
                        isLaptop={isLaptop}
                        onClick={() => navigate(getDynamicRoute(AppRoutes.contact, {}, `type=${CONTACT_TYPE_DEFAULT}`))}
                        fullWidth
                      >
                        <IconMail width={24} height={24} />
                      </BookingButton>
                    </Tooltip>
                  ) : (
                    <BookingButton
                      isLaptop={isLaptop}
                      onClick={() => navigate(getDynamicRoute(AppRoutes.contact, {}, `type=${CONTACT_TYPE_DEFAULT}`))}
                      fullWidth
                    >
                      <Stack flexDirection="row" alignItems="center" gap={1}>
                        <IconMail width={24} height={24} />
                        お問い合わせ
                      </Stack>
                    </BookingButton>
                  )}
                </Stack>
              );
            }
            return null;
          }

          // Collapsible parent group (e.g. 法定帳票): no own route, renders children.
          if ("children" in item) {
            const isGroupActive = item.children.some((child) => isRouteActive(child.relatedRoutes, pathname));
            // Laptop (icon-only): flatten children into standalone icons (each a leaf with
            // its own tooltip). No parent icon / flyout — the surrounding "|" dividers keep
            // the visual grouping.
            if (isLaptop) return <Fragment key={item.text}>{item.children.map((child) => renderLeafItem(child))}</Fragment>;

            const isExpanded = Boolean(expandedGroups[item.text]);
            return (
              <Fragment key={item.text}>
                <ListItemWrapper isLaptop={isLaptop} className={isGroupActive ? "active" : ""} onClick={() => toggleGroup(item.text)}>
                  <Stack flexDirection="row" alignItems="center" gap={1.5}>
                    {item.icon && <item.icon width={24} height={24} />}
                    <Typography variant="body14Bold" flex={1}>
                      {item.text}
                    </Typography>
                    <IconChevronDown
                      width={20}
                      height={20}
                      style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease-in-out" }}
                    />
                  </Stack>
                </ListItemWrapper>
                {isExpanded && item.children.map((child) => renderLeafItem(child, { indent: true }))}
              </Fragment>
            );
          }

          return renderLeafItem(item);
        })}
        {/* <Logo>
          <img src={sidebarLogo} alt="logo" />
        </Logo> */}
      </ListWrapper>
    ),
    [pathname, isLaptop, expandedGroups, navigate, renderLeafItem],
  );

  if (!isLaptop) return DrawerContent;
  return (
    <Drawer
      open={isOpenDrawer}
      onClose={toggleDrawer}
      hideBackdrop
      ModalProps={{ sx: { width: "fit-content" } }}
      PaperProps={{ sx: { bgcolor: background.main, height: "calc(100vh - 56px)", top: "56px" } }}
    >
      {/* <IconCloseWrapper>
        <IconCloseBtn onClick={toggleDrawer} />
      </IconCloseWrapper> */}
      {DrawerContent}
    </Drawer>
  );
};

const ListWrapper = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isLaptop",
})<{ isLaptop: boolean }>`
  padding: ${spacing.md} ${spacing.sm};
  width: ${({ isLaptop }) => (isLaptop ? "80px" : "auto")};
  min-width: ${({ isLaptop }) => (isLaptop ? "auto" : "242px")};
  background-color: ${background.main};
  color: ${text.white};
  min-height: calc(100vh - 56px);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 56px;
  z-index: ${zIndex.drawer};
`;

const ListItemWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isLaptop",
})<{ isLaptop: boolean }>`
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: ${rounded.sm};
  padding: ${({ isLaptop }) => (isLaptop ? `${spacing.xs} 0px` : `${spacing.xs} ${spacing.sm}`)};
  &.active {
    background-color: ${hexToRgba(background.white, 0.3)} !important;
  }

  &:hover {
    background-color: ${hexToRgba(background.white, 0.1)};
  }
`;

const BookingButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isLaptop",
})<{ isLaptop: boolean }>`
  min-width: ${({ isLaptop }) => (isLaptop ? "fit-content" : "64px")};
  min-height: 40px;
  background: transparent;
  border: 1px solid ${divider.lower};
  color: ${text.white};
  font-size: 14px;
  font-weight: bold;
  &:hover {
    /* background-color: ${button.hover_02}; */
    background-color: ${hexToRgba(background.white, 0.3)} !important;
  }
`;

const IconCloseWrapper = styled(Box)`
  background-color: ${background.main};
  height: 57px;
  border-bottom: 1px solid ${divider.lower};
  display: flex;
  align-items: center;
  padding-left: 16px;
`;

const IconCloseBtn = styled(IconClose)`
  font-size: 32px;
  color: ${text.white};
  transition: all 0.2s ease-in-out;
  border-radius: 32px;
  &:hover {
    cursor: pointer;
    background-color: ${hexToRgba(background.white, 0.3)};
  }
`;

const Logo = styled(Box)`
  position: absolute;
  bottom: 0;
  left: 0;
  pointer-events: none;
  user-select: none;
`;
